import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AvailableAgent } from "types";
import { Trash } from "lucide-react";
import { createColumnHelper } from "@tanstack/react-table";
import { ConfirmationDialog } from "components/dialog/confirmation";
import { Dialog } from "components/dialog";
import GenericTable from "components/table";
import Loader from "components/Loader";
import { useAssignedSpecialists } from "./useAssignedSpecialists";
import AssignSpecialistsForm from "./AssignSpecialistsForm";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<AvailableAgent>();

const AssignedSpecialists = () => {
  const { t } = useTranslation();
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    allSpecialists,
    assignedAgents,
    loading,
    error,
    isSaving,
    isDeleting,
    fetchData,
    assignSpecialists,
    removeSpecialist,
  } = useAssignedSpecialists({ agentId });

  const [specialistToRemove, setSpecialistToRemove] =
    useState<AvailableAgent | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  const handleRemoveClick = useCallback((specialist: AvailableAgent) => {
    setSpecialistToRemove(specialist);
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!specialistToRemove) return;

    setDeletingRowId(specialistToRemove.id);
    try {
      await removeSpecialist(specialistToRemove.id);
      setShowDeleteConfirmation(false);
      setSpecialistToRemove(null);
    } catch (err) {
      // Error handling is done in the hook
    } finally {
      setDeletingRowId(null);
    }
  }, [specialistToRemove, removeSpecialist]);

  const handleAssignSpecialists = useCallback(
    async (specialistIds: string[]) => {
      try {
        await assignSpecialists(specialistIds);
        setShowAssignDialog(false);
      } catch (err) {
        // Error handling is done in the hook
      }
    },
    [assignSpecialists]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => <p>Name</p>,
        cell: (info) => (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        ),
      }),
      columnHelper.accessor("id", {
        id: "actions",
        header: () => (
          <p className="p-4 text-left font-medium text-gray-900 dark:text-gray-100"></p>
        ),
        cell: (info) => {
          const agent = info.row.original;
          return (
            <button
              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveClick(agent);
              }}
              disabled={deletingRowId === agent.id}
              aria-label={`Remove ${agent.name}`}
            >
              <Trash size={15} className="text-red-500" />
            </button>
          );
        },
        meta: {
          cellClassName: "min-w-[50px]",
          headerClassName: "min-w-[50px]",
        },
      }),
    ],
    [deletingRowId, handleRemoveClick]
  );

  const renderRowClassName = useCallback(
    (row: any) => {
      const rowId = row.original.id;
      return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
    },
    [deletingRowId]
  );

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader
          txt={t("agents.assignedSpecialists.loadingAssignedSpecialists")}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => fetchData()}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <>
      <GenericTable<AvailableAgent>
        data={assignedAgents}
        columns={columns}
        onAdd={() => setShowAssignDialog(true)}
        addLabel={t("agents.assignedSpecialists.assignSpecialists")}
        searchPlaceholder={t(
          "agents.assignedSpecialists.searchAssignedSpecialists"
        )}
        renderRowClassName={renderRowClassName}
        onRowClick={(agent) => navigate(`/dashboard/agents/info/${agent.id}`)}
      />

      {/* Delete Confirmation Dialog */}
      {specialistToRemove && (
        <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={confirmDelete}
          title={t("agents.assignedSpecialists.confirmRemoval")}
          message={t("agents.assignedSpecialists.confirmRemovalMessage", {
            name: specialistToRemove.name,
          })}
          confirmText={
            isDeleting
              ? t("agents.assignedSpecialists.removing")
              : t("common.confirm")
          }
          cancelText={t("common.cancel")}
          variant="danger"
        />
      )}

      {/* Assign Agents Dialog */}
      <Dialog
        isOpen={showAssignDialog}
        setIsOpen={setShowAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        header={t("agents.assignedSpecialists.assignSpecialists")}
        className="w-full max-w-md !overflow-hidden"
      >
        <AssignSpecialistsForm
          allSpecialists={allSpecialists}
          assignedAgents={assignedAgents}
          onAssign={handleAssignSpecialists}
          isSaving={isSaving}
        />
      </Dialog>
    </>
  );
};

export default AssignedSpecialists;
